/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { remoteMcpSetServerScopePin } from "../funcs/remoteMcpSetServerScopePin.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RemoteMcpServerScopes } from "../models/components/remotemcpserverscopes.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetRemoteMcpServerScopePinRequest,
  SetRemoteMcpServerScopePinSecurity,
} from "../models/operations/setremotemcpserverscopepin.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetRemoteMcpServerScopePinMutationVariables = {
  request: SetRemoteMcpServerScopePinRequest;
  security?: SetRemoteMcpServerScopePinSecurity | undefined;
  options?: RequestOptions;
};

export type SetRemoteMcpServerScopePinMutationData = RemoteMcpServerScopes;

export type SetRemoteMcpServerScopePinMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setServerScopePin remoteMcp
 *
 * @remarks
 * Pin the scopes logins to a remote-backed MCP server's protected resource request, or clear the pin with an empty list. The pin belongs to the protected resource, so it applies to every server in the project with the same upstream URL, and the caller needs write access to all of them. Returns the scope state re-read after the write.
 */
export function useSetRemoteMcpServerScopePinMutation(
  options?: MutationHookOptions<
    SetRemoteMcpServerScopePinMutationData,
    SetRemoteMcpServerScopePinMutationError,
    SetRemoteMcpServerScopePinMutationVariables
  >,
): UseMutationResult<
  SetRemoteMcpServerScopePinMutationData,
  SetRemoteMcpServerScopePinMutationError,
  SetRemoteMcpServerScopePinMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetRemoteMcpServerScopePinMutation(client, options),
    ...options,
  });
}

export function mutationKeySetRemoteMcpServerScopePin(): MutationKey {
  return ["@gram/client", "remoteMcp", "setServerScopePin"];
}

export function buildSetRemoteMcpServerScopePinMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetRemoteMcpServerScopePinMutationVariables,
  ) => Promise<SetRemoteMcpServerScopePinMutationData>;
} {
  return {
    mutationKey: mutationKeySetRemoteMcpServerScopePin(),
    mutationFn: function setRemoteMcpServerScopePinMutationFn({
      request,
      security,
      options,
    }): Promise<SetRemoteMcpServerScopePinMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(remoteMcpSetServerScopePin(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
